// StartAssessmentDialogV2.jsx - Enhanced with Assessment Type & Inline Model Registration

import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Modal, Stack, Group, Text, Button, Card, Badge,
  Select, MultiSelect, Divider, Alert, Title, Grid, Switch, TextInput,
  ThemeIcon
} from '@mantine/core';
import { authFetch } from './utils/authFetch';
import { useForm } from '@mantine/form';
import {
  IconRobot, IconRocket, IconInfoCircle, IconPlus,
  IconCompass, IconGlobe, IconFileDescription, IconBolt
} from '@tabler/icons-react';
import { fetchGlobalConfig } from './redux/actions/globalConfigActions';
import { isRiskManager } from './utils/roleHelpers';
import { notifications } from '@mantine/notifications';
import {
  fetchAllAssessments, createAssessment,
  selectCompletedUseCaseAssessments
} from './redux/slices/assessmentsSlice';
import {
  fetchAllModels, createModel,
  selectModelsWithoutBuilderAssessment, selectModelsWithBuilderStatus
} from './redux/slices/modelsSlice';

const StartAssessmentDialogV2 = ({ open, onClose, preselectedModel = null }) => {
  const modelsWithoutBuilder = useSelector(selectModelsWithoutBuilderAssessment);
  const modelsWithBuilderStatus = useSelector(selectModelsWithBuilderStatus);
  const allTemplates = useSelector((state) => state.templates.allTemplates);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth0();

  // Check if user is Risk Manager
  const userIsRiskManager = useMemo(() => {
    const userRoles = user?.[`${window.location.origin}/roles`] || user?.['https://agenthuginn.ai/roles'] || user?.['https://riskgpt.app/roles'] || [];
    return isRiskManager(userRoles);
  }, [user]);

  // Assessment type: 'use_case' (Builder skill) | 'business_case' (Explorer skill).
  // The legacy 'builder' assessment_type is no longer reachable from the launcher;
  // baseline registers come from curated seeds (seeds/registers/*.json), not from
  // a full autopilot run.
  const [assessmentType, setAssessmentType] = useState('use_case');

  // Model source: 'existing' or 'new'
  const [modelSource, setModelSource] = useState('existing');

  // New model registration data
  const [newModelData, setNewModelData] = useState({
    modelName: '',
    useCaseShortDescription: '',
    modelType: '',
    modelStage: ''
  });

  const selectedMode = 'autopilot';
  const [isAutonomous, setIsAutonomous] = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('prompt');
  const [isStarting, setIsStarting] = useState(false);
  const [userEditedName, setUserEditedName] = useState(false);

  // Parent assessment UUID: Builder UUID for Builder, Builder UUID for Explorer
  const [parentAssessmentUuid, setParentAssessmentUuid] = useState('');

  // Step 4 register picker: which Type Baseline registers to apply as RAG context.
  const [availableRegisters, setAvailableRegisters] = useState([]);
  const [appliedRegisterIds, setAppliedRegisterIds] = useState([]);
  const [registersLoading, setRegistersLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRegistersLoading(true);
    authFetch('/.netlify/functions/listTypeBaselines')
      .then((r) => (r.ok ? r.json() : { baselines: [] }))
      .then((d) => setAvailableRegisters(d.baselines || []))
      .catch((err) => console.warn('[StartAssessmentDialog] register list failed:', err))
      .finally(() => setRegistersLoading(false));
  }, [open]);

  const form = useForm({
    initialValues: {
      selectedModel: preselectedModel?.modelId || '',
      assessmentName: '',
    },
    validate: {
      selectedModel: (value) => {
        if (modelSource === 'existing' && !value) return 'Please select a model';
        return null;
      },
      assessmentName: (value) => (!value || !value.trim() ? 'Assessment name is required' : null),
    },
  });

  // Ref to store form setFieldValue for stable useEffect callbacks
  const formRef = useRef(form);
  formRef.current = form;

  // Default register selection: matches the chosen model's model_type baseline.
  useEffect(() => {
    if (availableRegisters.length === 0) return;
    const selectedModelId = form.values.selectedModel;
    const model = (availableModels || []).find((m) => m.modelId === selectedModelId);
    const inferredType = model?.model_type;
    if (!inferredType) return;
    const defaults = availableRegisters
      .filter((b) => b.model_type === inferredType && b.overlay_slug === null)
      .map((b) => b.id);
    setAppliedRegisterIds((prev) => (prev.length > 0 ? prev : defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRegisters, form.values.selectedModel]);

  // Stable callbacks for form operations
  const setSelectedModel = useCallback((value) => {
    formRef.current.setFieldValue('selectedModel', value);
  }, []);

  const setAssessmentName = useCallback((value) => {
    formRef.current.setFieldValue('assessmentName', value);
  }, []);

  // Fetch model form config when dialog opens
  React.useEffect(() => {
    if (open) {
      dispatch(fetchGlobalConfig('modelFormConfig'));
    }
  }, [dispatch, open]);

  // Get available models based on assessment type
  const availableModels = useMemo(() => {
    if (assessmentType === 'builder') {
      // Builder: Only show models WITHOUT Builder assessments
      return modelsWithoutBuilder.map(model => ({
        ...model,
        modelId: model.model_uuid,
        modelName: model.model_name || 'Unnamed Model',
        modelType: model.model_type || 'Unknown Type',
        modelStage: model.model_stage || 'Unknown Stage',
        useCaseShortDescription: model.use_case_short_description || '',
      }));
    } else {
      // Builder/Explorer: Show ALL models (parent is selected separately)
      return modelsWithBuilderStatus.map(model => ({
        ...model,
        modelId: model.model_uuid,
        modelName: model.model_name || 'Unnamed Model',
        modelType: model.model_type || 'Unknown Type',
        modelStage: model.model_stage || 'Unknown Stage',
        useCaseShortDescription: model.use_case_short_description || '',
        builderAssessment: model.builderAssessment || null,
      }));
    }
  }, [assessmentType, modelsWithoutBuilder, modelsWithBuilderStatus]);

  // Get completed Builder assessments for Explorer parent selection
  const completedBuilders = useSelector(selectCompletedUseCaseAssessments);
  const completedBuilderOptions = useMemo(() => {
    if (assessmentType !== 'business_case') return [];
    return completedBuilders
      .map(a => ({
        value: a.info?.assessment_uuid,
        label: `${a.model_name || 'Unknown'} - ${a.info?.assessment_name || 'Builder Assessment'}`,
        modelUuid: a.model_uuid,
        modelName: a.model_name,
        assessmentUuid: a.info?.assessment_uuid,
        assessmentName: a.info?.assessment_name,
      }))
      .filter(a => a.value);
  }, [assessmentType, completedBuilders]);

  // Update form when preselectedModel changes
  useEffect(() => {
    if (preselectedModel) {
      setSelectedModel(preselectedModel.modelId);
      setAssessmentName(`${preselectedModel.modelName} Assessment`);
      setUserEditedName(false);
    }
  }, [preselectedModel, setSelectedModel, setAssessmentName]);

  // Auto-suggest assessment name when model or new model name changes
  useEffect(() => {
    if (userEditedName) return;

    if (modelSource === 'existing') {
      const modelId = form.values.selectedModel;
      if (modelId) {
        const model = availableModels.find(m => m.modelId === modelId);
        if (model) {
          setAssessmentName(`${model.modelName} Assessment`);
        }
      }
    } else if (newModelData.modelName) {
      setAssessmentName(`${newModelData.modelName} Assessment`);
    }
  }, [form.values.selectedModel, newModelData.modelName, modelSource, availableModels, userEditedName, setAssessmentName]);

  // Reset selections when assessment type changes
  useEffect(() => {
    setSelectedModel('');
    setUserEditedName(false);
    setParentAssessmentUuid('');
    setModelSource('existing');
  }, [assessmentType, setSelectedModel]);

  // Resolve the implicit skill template for the current assessmentType.
  // The launcher's Step 1 already picks the skill (Builder / Explorer); each
  // type maps to one published row in assessment_templates. This memo finds
  // that row so we can submit its template_id with the assessment.
  const resolvedTemplate = useMemo(() => {
    const templatesArray = allTemplates || [];
    const candidates = templatesArray.filter(t => t.is_published);
    const matching = candidates.filter(t => {
      if (assessmentType === 'use_case')      return t.model_type === 'use_case';
      if (assessmentType === 'business_case') return t.model_type === 'business_case' || t.model_type === 'explorer';
      // Type Baseline runs (admin) — match generative/predictive/custom legacy types.
      return t.model_type !== 'explorer' && t.model_type !== 'use_case' && t.model_type !== 'business_case';
    });
    // Prefer the default; otherwise the first published match.
    return matching.find(t => t.is_default) || matching[0] || null;
  }, [allTemplates, assessmentType]);

  const handleStartAssessment = async () => {
    // Validate form
    if (!form.isValid()) {
      form.validate();
      return;
    }

    // Validate new model data if registering new model (Builder only)
    if (assessmentType === 'builder' && modelSource === 'new') {
      if (!newModelData.modelName || !newModelData.modelType || !newModelData.modelStage) {
        notifications.show({
          title: 'Missing Fields',
          message: 'Please fill in all required model fields (Name, Type, Stage)',
          color: 'red'
        });
        return;
      }
    }

    // Explorer requires a completed Builder (use_case) parent assessment
    if (assessmentType === 'business_case' && !parentAssessmentUuid) {
      notifications.show({
        title: 'Builder Assessment Required',
        message: 'Please select a completed Builder assessment to link.',
        color: 'yellow'
      });
      return;
    }

    setIsStarting(true);

    try {
      let modelId;

      if (assessmentType === 'builder') {
        if (modelSource === 'new') {
          // Create new model first
          const modelResult = await dispatch(createModel({
            modelName: newModelData.modelName,
            useCaseShortDescription: newModelData.useCaseShortDescription,
            modelType: newModelData.modelType,
            modelStage: newModelData.modelStage,
          })).unwrap();

          modelId = modelResult.model_uuid;
        } else {
          modelId = form.values.selectedModel;
        }
      } else {
        // Builder/Explorer: auto-resolve modelId from parent assessment
        modelId = form.values.selectedModel;
      }

      // Determine parent UUID based on type
      const linkedParent = (assessmentType === 'use_case' || assessmentType === 'business_case')
        ? (parentAssessmentUuid || null)
        : null;

      // Determine starting position based on type
      let startPosition = { majorStep: selectedMode === 'autopilot' ? 'upload' : 'useCase' };
      let startStep = selectedMode === 'autopilot' ? 'upload' : 'contextAssessment';
      if (assessmentType === 'use_case') {
        startPosition = { majorStep: 'brief' };
        startStep = 'brief';
      }

      // Create assessment with type
      const result = await dispatch(createAssessment({
        modelId,
        assessmentName: form.values.assessmentName.trim(),
        templateId: resolvedTemplate?.id,
        mode: selectedMode,
        autonomous: selectedMode === 'autopilot' ? isAutonomous : null,
        assessmentMode: selectedMode === 'autopilot' ? assessmentMode : 'prompt',
        currentStep: startStep,
        position: startPosition,
        assessmentType,
        parentAssessmentUuid: linkedParent,
        appliedRegisterIds,
      })).unwrap();

      const assessmentUuid = result.assessmentUuid;

      // Refresh data
      await Promise.all([
        dispatch(fetchAllModels()).unwrap(),
        dispatch(fetchAllAssessments()).unwrap()
      ]);

      onClose();

      // Navigate based on mode and assessment type
      if (assessmentType === 'use_case') {
        // Builder assessments use their own flow
        navigate(`/builder-assessment/${modelId}/${assessmentUuid}`);
      } else if (assessmentType === 'business_case') {
        // Explorer (business_case) assessments use Explorer autopilot flow
        navigate(`/explorer-assessment/${modelId}/${assessmentUuid}`);
      } else {
        navigate(`/autopilot-assessment/${assessmentUuid}`);
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
      notifications.show({
        title: 'Error',
        message: `Failed to start assessment: ${error.message}`,
        color: 'red'
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Model options with Builder status indicator for Explorer mode
  const modelOptions = availableModels.map(model => ({
    value: model.modelId,
    label: assessmentType === 'explorer' && model.builderAssessment
      ? `${model.modelName} (Builder ${model.builderAssessment.isCompleted ? '✓' : '◐'})`
      : `${model.modelName} (${model.modelType})`,
  }));

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Group gap="md" align="center">
          <IconRocket size={24} color="#1976d2" />
          <Title order={3}>Start New Risk Assessment</Title>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="xl">
        {/* 1. Skill Selection */}
        <Stack gap="md">
          <Text fw={600} size="md">1. Choose a Skill</Text>
            <Grid>
              <Grid.Col span={6} style={{ display: 'flex' }}>
                <Card
                  withBorder
                  radius="md"
                  p="lg"
                  style={{
                    cursor: 'pointer',
                    border: assessmentType === 'use_case' ? '2px solid #1976d2' : '1px solid #dee2e6',
                    backgroundColor: assessmentType === 'use_case' ? '#f0f7ff' : 'white',
                    flex: 1
                  }}
                  onClick={() => setAssessmentType('use_case')}
                >
                  <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                    <IconCompass size={28} color={assessmentType === 'use_case' ? '#1976d2' : '#adb5bd'} />
                    <Text fw={700} size="md" c={assessmentType === 'use_case' ? 'blue' : 'dimmed'}>Builder</Text>
                    <Text size="xs" ta="center" c="dimmed">
                      Use-case-specific AI risks
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={6} style={{ display: 'flex' }}>
                <Card
                  withBorder
                  radius="md"
                  p="lg"
                  style={{
                    cursor: 'pointer',
                    border: assessmentType === 'business_case' ? '2px solid #40c057' : '1px solid #dee2e6',
                    backgroundColor: assessmentType === 'business_case' ? '#ebfbee' : 'white',
                    flex: 1
                  }}
                  onClick={() => setAssessmentType('business_case')}
                >
                  <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                    <IconGlobe size={28} color={assessmentType === 'business_case' ? '#40c057' : '#adb5bd'} />
                    <Text fw={700} size="md" c={assessmentType === 'business_case' ? 'green' : 'dimmed'}>Explorer</Text>
                    <Text size="xs" ta="center" c="dimmed">
                      Business-impact framing: financial, regulatory, operational
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
        </Stack>

        <Divider />

        {/* Conditional flows by type */}

        {assessmentType === 'builder' && (
          <>
            {/* Builder: Step 2 - Model Selection */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Select Your Model</Text>

              {/* Model Source Toggle */}
              <Group gap="md">
                <Button
                  variant={modelSource === 'existing' ? 'filled' : 'light'}
                  color="blue"
                  size="sm"
                  onClick={() => setModelSource('existing')}
                >
                  Choose Existing Model
                </Button>
                <Button
                  variant={modelSource === 'new' ? 'filled' : 'light'}
                  color="green"
                  size="sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setModelSource('new')}
                >
                  Register New Model
                </Button>
              </Group>

              {modelSource === 'existing' ? (
                <>
                  <Select
                    placeholder="Choose a model to assess..."
                    data={modelOptions}
                    {...form.getInputProps('selectedModel')}
                    searchable
                    size="md"
                  />
                  {availableModels.length === 0 && (
                    <Alert color="blue" icon={<IconInfoCircle size="1rem" />}>
                      <Text size="sm">
                        All models already have Builder assessments. Register a new model or switch to Builder/Explorer mode.
                      </Text>
                    </Alert>
                  )}
                </>
              ) : (
                <Card withBorder p="md" radius="md" bg="gray.0">
                  <Stack gap="md">
                    <Text fw={600} size="sm" c="green">Register New Model</Text>

                    <TextInput
                      label="Model Name"
                      required
                      placeholder="Enter model name..."
                      value={newModelData.modelName}
                      onChange={(e) => setNewModelData(prev => ({
                        ...prev, modelName: e.target.value
                      }))}
                    />

                    <TextInput
                      label="Short Description"
                      placeholder="Brief description of the model..."
                      value={newModelData.useCaseShortDescription}
                      onChange={(e) => setNewModelData(prev => ({
                        ...prev, useCaseShortDescription: e.target.value
                      }))}
                    />

                    <Group grow>
                      <Select
                        label="Model Type"
                        required
                        placeholder="Select type..."
                        data={['Generative AI', 'Predictive AI', 'Descriptive AI']}
                        value={newModelData.modelType}
                        onChange={(value) => setNewModelData(prev => ({
                          ...prev, modelType: value
                        }))}
                      />

                      <Select
                        label="Model Stage"
                        required
                        placeholder="Select stage..."
                        data={['Design', 'Development', 'Test', 'Inference/Production', 'Re-training']}
                        value={newModelData.modelStage}
                        onChange={(value) => setNewModelData(prev => ({
                          ...prev, modelStage: value
                        }))}
                      />
                    </Group>

                    <Divider label="Documentation" labelPosition="center" />

                    <Group gap="xs" align="flex-start" p="xs" style={{
                      backgroundColor: 'var(--mantine-color-blue-0)',
                      borderRadius: '8px'
                    }}>
                      <ThemeIcon size="sm" color="blue" variant="light" radius="xl">
                        <IconFileDescription size={14} />
                      </ThemeIcon>
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text size="xs" c="blue.7" fw={500}>
                          Model Documentation
                        </Text>
                        <Text size="xs" c="dimmed">
                          You can upload model card / documentation during the Autopilot workflow, or add it later from Model Inventory.
                        </Text>
                      </Stack>
                    </Group>

                    <Text size="xs" c="dimmed">
                      Model will be saved to Inventory when assessment starts.
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>

            <Divider />

            {/* Builder: Step 3 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Name Your Assessment</Text>
              <TextInput
                placeholder="Enter a unique name for this assessment..."
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                description="Give this assessment a descriptive name (auto-generated from model)"
              />
            </Stack>
          </>
        )}

        {assessmentType === 'use_case' && (
          <>
            {/* Builder: Step 2 - Model */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Select Your Model</Text>
              <Select
                placeholder="Choose a model..."
                data={modelOptions}
                {...form.getInputProps('selectedModel')}
                searchable
                size="md"
                description="Pick the model this brief will evaluate"
              />
            </Stack>

            <Divider />

            {/* Builder: Step 3 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Name Your Assessment</Text>
              <TextInput
                placeholder="e.g., Customer Onboarding Builder Assessment"
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                required
                description="Give this Builder assessment a descriptive name"
              />
            </Stack>
          </>
        )}

        {assessmentType === 'business_case' && (
          <>
            {/* Explorer: Step 2 - Name */}
            <Stack gap="md">
              <Text fw={600} size="md">2. Name Your Assessment</Text>
              <TextInput
                placeholder="e.g., Fraud Detection Business Impact Assessment"
                {...form.getInputProps('assessmentName')}
                onChange={(event) => {
                  form.setFieldValue('assessmentName', event.currentTarget.value);
                  setUserEditedName(true);
                }}
                size="md"
                required
                description="Give this Explorer assessment a descriptive name"
              />
            </Stack>

            <Divider />

            {/* Explorer: Step 3 - Select Completed Builder */}
            <Stack gap="md">
              <Text fw={600} size="md">3. Link to Builder Assessment</Text>
              <Text size="xs" c="dimmed">
                Explorer assessments evaluate business impact based on a completed Builder assessment.
              </Text>

              {completedBuilderOptions.length > 0 ? (
                <>
                  <Select
                    placeholder="Choose a completed Builder assessment..."
                    data={completedBuilderOptions}
                    value={parentAssessmentUuid}
                    onChange={(value) => {
                      setParentAssessmentUuid(value || '');
                      const selected = completedBuilderOptions.find(a => a.value === value);
                      if (selected) {
                        form.setFieldValue('selectedModel', selected.modelUuid);
                      }
                    }}
                    searchable
                    size="md"
                  />

                  {parentAssessmentUuid && (
                    <Alert color="green" icon={<IconGlobe size="1rem" />} variant="light">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>
                          Use-case risks will be imported from the linked Builder
                        </Text>
                        <Text size="xs" c="dimmed">
                          Scenario-specific risks from the Builder assessment provide context for business-impact analysis.
                        </Text>
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert color="yellow" icon={<IconInfoCircle size="1rem" />}>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>No completed Builder assessments found</Text>
                    <Text size="xs" c="dimmed">
                      Complete a Builder assessment first to create an Explorer assessment.
                    </Text>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </>
        )}

        <Divider />

        {/* 4. Risk registers to apply (RAG context) */}
        <Stack gap="md">
          <Text fw={600} size="md">4. Risk registers to apply</Text>
          <Text size="xs" c="dimmed">
            The selected baseline registers are surfaced to the AI as context during risk
            generation. We pre-tick the register matching this model's type; tick overlays
            (e.g. open-weight, closed-weight, tabular) that fit the deployment.
          </Text>
          <MultiSelect
            placeholder={registersLoading ? 'Loading registers…' : 'No registers selected'}
            data={availableRegisters.map((r) => {
              const overlay = r.overlay_slug ? ` · ${r.overlay_slug}` : '';
              return {
                value: r.id,
                label: `${r.model_type}${overlay} (v${r.version})`,
              };
            })}
            value={appliedRegisterIds}
            onChange={setAppliedRegisterIds}
            searchable
            clearable
            disabled={registersLoading || availableRegisters.length === 0}
            description={availableRegisters.length === 0 ? 'No baseline registers seeded yet — risk generation will use frameworks + catalogues only.' : undefined}
            size="md"
          />
        </Stack>

        <Divider />

        {/* 5. Autopilot Execution Mode */}
        {(selectedMode === 'autopilot' || !userIsRiskManager) && (
          <Stack gap="md">
            <Text fw={600} size="md">5. Autopilot Execution Mode</Text>
            <Group gap="md" align="center" wrap="nowrap" style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: 'var(--mantine-color-gray-0)',
              border: '1px solid var(--mantine-color-gray-3)',
              width: '100%',
              justifyContent: 'space-between'
            }}>
              <Stack gap={4} style={{ flex: 1 }}>
                <Text size="sm" fw={600} c={!isAutonomous ? "blue" : "dimmed"}>
                  Supervised
                </Text>
                <Text size="xs" c="dimmed">
                  Pause after each risk area for approval
                </Text>
              </Stack>

              <Switch
                checked={isAutonomous}
                onChange={(event) => setIsAutonomous(event.currentTarget.checked)}
                color="green"
                size="md"
                styles={{
                  track: {
                    backgroundColor: isAutonomous ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-blue-6)',
                    cursor: 'pointer'
                  }
                }}
              />

              <Stack gap={4} style={{ flex: 1, alignItems: 'flex-end' }}>
                <Text size="sm" fw={600} c={isAutonomous ? "green" : "dimmed"}>
                  Autonomous
                </Text>
                <Text size="xs" c="dimmed" ta="right">
                  Process all areas automatically
                </Text>
              </Stack>
            </Group>
          </Stack>
        )}

        {/* 4b. Assessment Engine - Only for Risk Managers in Autopilot */}
        {userIsRiskManager && selectedMode === 'autopilot' && (
          <>
            <Divider />
            <Stack gap="md">
              <Text fw={600} size="md">Assessment Engine</Text>
              <Grid>
                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: assessmentMode === 'prompt' ? '2px solid #1976d2' : '1px solid #dee2e6',
                      backgroundColor: assessmentMode === 'prompt' ? '#f0f7ff' : 'white',
                      minHeight: '140px'
                    }}
                    onClick={() => setAssessmentMode('prompt')}
                  >
                    <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                      <IconBolt size={28} color={assessmentMode === 'prompt' ? '#1976d2' : '#adb5bd'} />
                      <Text fw={700} size="md" c={assessmentMode === 'prompt' ? 'blue' : 'dimmed'}>Prompt Pipeline</Text>
                      <Text size="xs" ta="center" c="dimmed">
                        Each risk area runs through a fixed sequence of prompts. Fast and predictable.
                      </Text>
                      {assessmentMode === 'prompt' && (
                        <Badge color="blue" variant="light" size="sm">Selected</Badge>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Card
                    withBorder
                    radius="md"
                    p="lg"
                    style={{
                      cursor: 'pointer',
                      border: assessmentMode === 'agentic' ? '2px solid #7950f2' : '1px solid #dee2e6',
                      backgroundColor: assessmentMode === 'agentic' ? '#f3f0ff' : 'white',
                      minHeight: '140px'
                    }}
                    onClick={() => setAssessmentMode('agentic')}
                  >
                    <Stack gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                      <IconRobot size={28} color={assessmentMode === 'agentic' ? '#7950f2' : '#adb5bd'} />
                      <Text fw={700} size="md" c={assessmentMode === 'agentic' ? 'violet' : 'dimmed'}>Agentic Pipeline</Text>
                      <Text size="xs" ta="center" c="dimmed">
                        AI agents collaborate autonomously with quality loops and tool access. More thorough, takes longer.
                      </Text>
                      {assessmentMode === 'agentic' && (
                        <Badge color="violet" variant="light" size="sm">Selected</Badge>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </>
        )}

        {/* Action Buttons */}
        <Group justify="space-between">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>

          <Button
            onClick={handleStartAssessment}
            loading={isStarting}
            leftSection={<IconRobot size={16} />}
            size="md"
            color={assessmentType === 'business_case' ? 'green' : 'blue'}
          >
            {isStarting
              ? 'Starting...'
              : assessmentType === 'builder'
                ? 'Generate Risk Register'
                : `Start ${assessmentType === 'use_case' ? 'Builder' : 'Explorer'} Assessment`
            }
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StartAssessmentDialogV2;
